import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder } from '@fortawesome/free-solid-svg-icons';

interface BreadcrumbPathProps {
  path: string;
  folders?: any[];
  folderId?: string | null;
  onFolderClick?: (folderId: string | null) => void;
  showIcon?: boolean;
  clickable?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * BreadcrumbPath Component
 * Displays folder path as clickable breadcrumbs with consistent design
 * Format: 📁 /sheshkumar/anant/sarita test/siddharth/vaibhav/shruti
 */
const BreadcrumbPath: React.FC<BreadcrumbPathProps> = ({
  path,
  folders = [],
  folderId,
  onFolderClick,
  showIcon = true,
  clickable = false,
  className = '',
  style = {}
}) => {
  // Build folder hierarchy with IDs for clickable breadcrumbs
  const buildFolderHierarchy = (targetFolderId: string | null): Array<{ id: string | null; name: string }> => {
    if (!targetFolderId) return [{ id: null, name: '' }];
    
    const hierarchy: Array<{ id: string | null; name: string }> = [];
    let currentId: string | null = targetFolderId;
    
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        hierarchy.unshift({ id: folder.id, name: folder.name });
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    
    return hierarchy;
  };

  // If folderId is provided and folders array exists, build clickable breadcrumbs
  if (folderId !== undefined && folders.length > 0 && clickable && onFolderClick) {
    const hierarchy = buildFolderHierarchy(folderId);
    
    return (
      <div 
        className={`breadcrumb-path-display ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.85rem',
          fontFamily: 'monospace',
          color: '#495057',
          ...style
        }}
      >
        {showIcon && (
          <FontAwesomeIcon 
            icon={faFolder} 
            style={{ color: '#6c757d', marginRight: '0.25rem' }} 
          />
        )}
        <span style={{ color: '#6c757d' }}>/</span>
        {hierarchy.map((item, index) => (
          <React.Fragment key={index}>
            <span
              onClick={() => onFolderClick(item.id)}
              style={{
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
              title={`Navigate to ${item.name || 'root'}`}
            >
              {item.name}
            </span>
            {index < hierarchy.length - 1 && (
              <span style={{ color: '#6c757d' }}>/</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Otherwise, display static path
  const segments = path.split('/').filter(s => s);
  
  return (
    <div 
      className={`breadcrumb-path-display ${className}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
        color: '#495057',
        ...style
      }}
    >
      {showIcon && (
        <FontAwesomeIcon 
          icon={faFolder} 
          style={{ color: '#6c757d', marginRight: '0.25rem' }} 
        />
      )}
      <span style={{ color: '#6c757d' }}>/</span>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          <span style={{ color: '#007bff' }}>{segment}</span>
          {index < segments.length - 1 && (
            <span style={{ color: '#6c757d' }}>/</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default BreadcrumbPath;
